import type { ApiKey, ApiKeyScope, Webhook, WebhookDeliveryStatus, WebhookEventType } from '@/types/api'

/** Same rule as the server: exact type, `group.*`, or `*`. */
export function patternMatches(pattern: string, type: string): boolean {
  return pattern === '*' || pattern === type || (pattern.endsWith('.*') && type.startsWith(pattern.slice(0, -1)))
}

/** Event groups for the picker: `display.*`, `player.*`, … with their types. */
export function eventGroups(types: WebhookEventType[]): { group: string; pattern: string; types: WebhookEventType[] }[] {
  const groups = new Map<string, WebhookEventType[]>()
  for (const t of types) {
    const group = t.type.split('.')[0]
    groups.set(group, [...(groups.get(group) ?? []), t])
  }
  return [...groups.entries()].map(([group, list]) => ({ group, pattern: `${group}.*`, types: list }))
}

/** Toggle one subscription entry, collapsing to the wildcard when every type of a group is selected. */
export function toggleEvent(selected: string[], value: string, allTypes: WebhookEventType[]): string[] {
  const has = selected.includes(value)
  let next = has ? selected.filter((s) => s !== value) : [...selected, value]
  if (value.endsWith('.*') && !has) {
    // Selecting a wildcard replaces its individual types.
    next = next.filter((s) => s === value || s.endsWith('*') || !patternMatches(value, s))
  }
  const group = value.split('.')[0]
  const groupTypes = allTypes.filter((t) => t.type.startsWith(`${group}.`)).map((t) => t.type)
  if (!value.endsWith('*') && groupTypes.length > 1 && groupTypes.every((t) => next.includes(t))) {
    next = [...next.filter((s) => !groupTypes.includes(s)), `${group}.*`]
  }
  return next
}

/** Whether `type` is covered by the selection (directly or by a wildcard). */
export function isSubscribed(selected: string[], type: string): boolean {
  return selected.some((p) => patternMatches(p, type))
}

export const DELIVERY_TONE: Record<WebhookDeliveryStatus, 'success' | 'warning' | 'danger' | 'secondary'> = {
  succeeded: 'success',
  pending: 'secondary',
  retrying: 'warning',
  failed: 'danger',
  dead: 'danger',
}

export type WebhookHealth = 'healthy' | 'failing' | 'disabled' | 'new'

export function webhookHealth(w: Pick<Webhook, 'is_active' | 'consecutive_failures' | 'last_success_at'>): WebhookHealth {
  if (!w.is_active) return 'disabled'
  if (w.consecutive_failures > 0) return 'failing'
  return w.last_success_at ? 'healthy' : 'new'
}

export function isValidWebhookUrl(url: string, allowHttp = false): boolean {
  try {
    const u = new URL(url)
    return (u.protocol === 'https:' || (allowHttp && u.protocol === 'http:')) && !u.username && !u.password && u.hostname.length > 0
  } catch {
    return false
  }
}

/** Scopes the current user may grant first, preserving server order. */
export function sortScopes(scopes: ApiKeyScope[]): ApiKeyScope[] {
  return [...scopes].sort((a, b) => Number(b.grantable) - Number(a.grantable))
}

export function keyStatusTone(status: ApiKey['status']): 'success' | 'secondary' | 'danger' {
  return status === 'active' ? 'success' : status === 'expired' ? 'secondary' : 'danger'
}

/** ISO timestamp `days` from now (end of that day), or null for "never". */
export function expiryFromDays(days: number | null, now = new Date()): string | null {
  if (days === null) return null
  const d = new Date(now.getTime() + days * 86_400_000)
  d.setUTCHours(23, 59, 59, 0)
  return d.toISOString()
}

/** Parses the allowed-IPs textarea (comma / whitespace separated). */
export function parseIpList(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/* ---------------------------------------------------------------- branding */

export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)
}

/** Readable text colour (WCAG relative luminance) on top of `hex`. */
export function foregroundFor(hex: string): '#ffffff' | '#111111' {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 0.4 ? '#111111' : '#ffffff'
}

/** CSS custom properties that re-theme the web app with the organisation's accent colour. */
export function accentVariables(accent: string | null | undefined): Record<string, string> | null {
  if (!isHexColor(accent)) return null
  return {
    '--primary': accent,
    '--primary-hover': `color-mix(in oklch, ${accent}, black 10%)`,
    '--primary-foreground': foregroundFor(accent),
    '--ring': `color-mix(in oklch, ${accent} 45%, transparent)`,
  }
}
